export type HardFailInfo = {
  reason: string;
  ok: number;
  bad: number;
  url?: string;
  docId: string | null;
  layerId: string;
  layerLabel: string;
  reportAllowed: boolean;
  nextReportAt: number | null;
  consecutiveFailCycles: number;
  event: "hardfail" | "recover";
};

type MonitorOptions = {
  failRatio?: number;
  minErrorTiles?: number;
  consecutiveFailsRequired?: number;
  watchdogMs?: number;
  reportCooldownMs?: number;
  confirmFailGraceMs?: number;
  postRecoverMuteMs?: number;

  // NEW:
  tileTimeoutMs?: number;          // per tile hung timeout (treat as error)
  noActivityFailMs?: number;       // if nothing happens in cycle, treat as fail
  treatNoActivityAsFail?: boolean; // default true
};

function attachLayerHealthMonitor(
  layer: L.TileLayer.WMS,
  docId: string | null,
  layerId: string,
  layerLabel: string,
  onEvent: (info: HardFailInfo) => void,
  opts: MonitorOptions = {}
) {
  const FAIL_RATIO = opts.failRatio ?? 0.7;
  const MIN_ERROR_TILES = opts.minErrorTiles ?? 4;
  const CONSECUTIVE_FAILS_REQUIRED = opts.consecutiveFailsRequired ?? 1;
  const WATCHDOG_MS = opts.watchdogMs ?? 10_000;
  const REPORT_COOLDOWN_MS = opts.reportCooldownMs ?? 60_000;

  const CONFIRM_FAIL_GRACE_MS = opts.confirmFailGraceMs ?? 5_000;
  const POST_RECOVER_MUTE_MS = opts.postRecoverMuteMs ?? 20_000;

  // NEW defaults
  const TILE_TIMEOUT_MS = opts.tileTimeoutMs ?? 15_000;                 // good starting point
  const TREAT_NO_ACTIVITY_AS_FAIL = opts.treatNoActivityAsFail ?? true;
  const NO_ACTIVITY_FAIL_MS = opts.noActivityFailMs ?? WATCHDOG_MS;     // tie it to watchdog by default

  let ok = 0, bad = 0;
  let consecutiveFailCycles = 0;
  let watchdog: number | null = null;
  let lastReportAt = 0;
  let previouslyFailed = false;

  // NEW: pending confirmation + post-recover mute
  let pendingFailTimer: number | null = null;
  let pendingSnapshot: { ok: number; bad: number; reason: string } | null = null;
  let postRecoverMuteUntil = 0;

  // NEW: activity tracking (for "no activity" detection)
  let anyActivity = false;
  let noActivityTimer: number | null = null;

  // ---- Helpers
  const clearWatchdog = () => {
    if (watchdog) {
      clearTimeout(watchdog);
      watchdog = null;
    }
  };

  const clearNoActivityTimer = () => {
    if (noActivityTimer) {
      clearTimeout(noActivityTimer);
      noActivityTimer = null;
    }
  };

  const clearPendingFail = () => {
    if (pendingFailTimer) {
      clearTimeout(pendingFailTimer);
      pendingFailTimer = null;
    }
    pendingSnapshot = null;
  };

  const emitRecover = () => {
    previouslyFailed = false;
    clearPendingFail();
    postRecoverMuteUntil = Date.now() + POST_RECOVER_MUTE_MS;

    onEvent({
      reason: "Layer recovered",
      ok, bad,
      url: (layer as any)._url,
      docId,
      layerId, layerLabel,
      reportAllowed: false,
      nextReportAt: null,
      consecutiveFailCycles,
      event: "recover",
    });
  };

  const emitHardFail = (reason: string, snapshot?: { ok: number; bad: number }) => {
    previouslyFailed = true;
    const now = Date.now();

    const reportAllowed = (now - lastReportAt) >= REPORT_COOLDOWN_MS;
    const nextReportAt = reportAllowed ? null : (lastReportAt + REPORT_COOLDOWN_MS);
    if (reportAllowed) lastReportAt = now;

    onEvent({
      reason,
      ok: snapshot?.ok ?? ok,
      bad: snapshot?.bad ?? bad,
      url: (layer as any)._url,
      docId,
      layerId, layerLabel,
      reportAllowed,
      nextReportAt,
      consecutiveFailCycles,
      event: "hardfail",
    });
  };

  // ---- Cycle control
  const startCycle = () => {
    ok = 0;
    bad = 0;
    anyActivity = false;

    clearWatchdog();
    clearNoActivityTimer();

    // NEW: if literally nothing happens in this cycle (no tileload/tileerror),
    // treat as unreachable / hung / queued forever.
    if (TREAT_NO_ACTIVITY_AS_FAIL) {
      noActivityTimer = window.setTimeout(() => {
        // still no activity? call evalCycle with an explicit reason
        if (!anyActivity) {
          evalCycle("No tile activity (requests hanging / service unreachable)");
        }
      }, NO_ACTIVITY_FAIL_MS);
    }

    watchdog = window.setTimeout(() => {
      // Keep your old watchdog behavior as an extra safety net:
      if (ok === 0 && bad >= MIN_ERROR_TILES) {
        // This watchdog indicates we only saw failures quickly
        evalCycle("Only failing tiles (watchdog)");
      }
    }, WATCHDOG_MS);
  };

  const evalCycle = (reasonHint?: string) => {
    clearWatchdog();
    clearNoActivityTimer();

    const total = ok + bad;

    const cycleFailed =
      // existing rules:
      (ok === 0 && bad >= MIN_ERROR_TILES) ||
      (total >= MIN_ERROR_TILES && bad / total >= FAIL_RATIO) ||
      // NEW rule: no activity is considered a failure (if enabled)
      (TREAT_NO_ACTIVITY_AS_FAIL && total === 0 && !!reasonHint);

    if (cycleFailed) {
      consecutiveFailCycles++;
    } else {
      consecutiveFailCycles = 0;
    }

    // RECOVER
    if (previouslyFailed && !cycleFailed) {
      emitRecover();
      return;
    }

    // Only consider a hard fail once threshold cycles reached
    if (consecutiveFailCycles >= CONSECUTIVE_FAILS_REQUIRED) {
      const reason =
        reasonHint ??
        (total === 0
          ? "No tile activity (requests hanging / service unreachable)"
          : ok === 0
            ? "No successful tiles"
            : "High tile error ratio");

      // post-recover mute window
      if (Date.now() < postRecoverMuteUntil) return;

      // Queue confirmation; cancel if next cycle recovers
      if (!pendingFailTimer) {
        pendingSnapshot = { ok, bad, reason };

        pendingFailTimer = window.setTimeout(() => {
          pendingFailTimer = null;

          emitHardFail(
            pendingSnapshot?.reason ?? reason,
            pendingSnapshot ? { ok: pendingSnapshot.ok, bad: pendingSnapshot.bad } : undefined
          );

          pendingSnapshot = null;
        }, CONFIRM_FAIL_GRACE_MS);
      }
    } else {
      // Didn’t meet threshold → cancel any pending hardfail
      clearPendingFail();
    }
  };

  // ---- NEW: Tile timeout patch (turn hanging tiles into tileerror)
  // This keeps your existing counting and eval logic intact.
  const patchTileTimeout = () => {
    const anyLayer = layer as any;
    const originalCreateTile = anyLayer.createTile?.bind(layer);

    if (!originalCreateTile) return () => {};

    anyLayer.createTile = (coords: any, done: any) => {
      const tile: HTMLImageElement = originalCreateTile(coords, done);

      // Some Leaflet versions pass a <img> that doesn’t have complete listeners yet.
      // We attach our own timeout and clear it on load/error.
      let cleared = false;
      const clear = () => {
        if (cleared) return;
        cleared = true;
        window.clearTimeout(timer);
      };

      const timer = window.setTimeout(() => {
        if (cleared) return;
        clear();

        // Mark as timeout for debugging
        (tile as any)._leafletTimeout = true;

        // Abort the request (best-effort)
        try {
          tile.src = ""; // stops pending request in most browsers
        } catch {}

        // Signal to Leaflet: treat as tile error
        const err = new Error("Tile request timed out");
        done(err, tile);

        // Also emit tileerror so your existing handler sees it
        layer.fire("tileerror", { error: err, tile, coords });
      }, TILE_TIMEOUT_MS);

      tile.addEventListener("load", clear);
      tile.addEventListener("error", clear);

      return tile;
    };

    return () => {
      anyLayer.createTile = originalCreateTile;
    };
  };

  const detachTileTimeoutPatch = patchTileTimeout();

  // ---- Leaflet event handlers
  const onLoading = () => startCycle();

  const onTileLoad = () => {
    ok++;
    anyActivity = true;
    // any success cancels pending hardfail
    if (pendingFailTimer) clearPendingFail();
  };

  const onTileError = () => {
    bad++;
    anyActivity = true;
  };

  const onLoad = () => {
    evalCycle();
  };

  layer.on("loading", onLoading);
  layer.on("tileload", onTileLoad);
  layer.on("tileerror", onTileError);
  layer.on("load", onLoad);

  const detach = () => {
    clearWatchdog();
    clearNoActivityTimer();
    clearPendingFail();

    layer.off("loading", onLoading);
    layer.off("tileload", onTileLoad);
    layer.off("tileerror", onTileError);
    layer.off("load", onLoad);

    detachTileTimeoutPatch();
  };

  return detach;
}

export default attachLayerHealthMonitor;
